import {
  PaymentStatementPayment,
  PaymentStatementPurchase,
  PaymentStatementRefundItem,
} from '@models/paymentStatement';
import { SalesChannelsType } from '@models/sales-channels';
import { dateToUtcDate, isValidDateString } from './date';

export const NAVER_PAYED_PARTNER_REFUND_BLOCKED_MESSAGE =
  '파트너가 취소할 수 없는 단계의 주문건입니다. 고객과 먼저 소통한 뒤 파트너스 채널에 조치 여부를 남겨주세요.';

export const getNaverProductOrderStatus = (order?: { channel?: SalesChannelsType; origin?: any }) => {
  if (order?.channel !== SalesChannelsType.NAVER_STORE) {
    return undefined;
  }
  const origin = order.origin;
  return (
    origin?.content?.productOrder?.productOrderStatus ||
    origin?.productOrder?.productOrderStatus ||
    origin?.productOrderStatus ||
    origin?.orderStatus
  );
};

export const isNaverPayedPartnerRefundBlocked = (order?: { channel?: SalesChannelsType; origin?: any }) =>
  getNaverProductOrderStatus(order) === 'PAYED';

type PaymentMethodType =
  | 'card'
  | 'trans'
  | 'vbank'
  | 'phone'
  | 'paypal'
  | 'applepay'
  | 'naverpay'
  | 'samsung'
  | 'kpay'
  | 'kakaopay'
  | 'payco'
  | 'lpay'
  | 'ssgpay'
  | 'tosspay'
  | 'cultureland'
  | 'smartculture'
  | 'happymoney'
  | 'booknlife'
  | 'point'
  | 'wechat'
  | 'alipay'
  | 'unionpay'
  | 'tenpay';

export interface PaymentMethod {
  type: PaymentMethodType;
  label: string;
  paymentGateway: string;
}

export const PAYMENT_METHODS: PaymentMethod[] = [
  {
    type: 'card',
    label: '신용/체크카드',
    paymentGateway: 'nice',
  },
  {
    type: 'kakaopay',
    label: '카카오페이',
    paymentGateway: 'nice',
  },
  {
    type: 'naverpay',
    label: '네이버페이',
    paymentGateway: 'naverpay',
  },
];

export const PAYMENT_METHODS_DICT: { [key: string]: PaymentMethod } = {
  card: {
    type: 'card',
    label: '신용/체크카드',
    paymentGateway: 'nice',
  },
  kakaopay: {
    type: 'kakaopay',
    label: '카카오페이',
    paymentGateway: 'nice',
  },
  naverpay: {
    type: 'naverpay',
    label: '네이버페이',
    paymentGateway: 'naverpay',
  },
};

export const extractDateFromStrings = (
  array: string[] = [],
): {
  matchDays: string[];
  matchTimes: string[];
  matches: string[];
  notMatches: string[];
} => {
  const regexDate = /\d{1,2}\/\d{1,2}/g;
  const regexDate3 = /\d{1,2}-\d{1,2}-\d{1,2}/;
  const regexTime = /\d{1,2}:\d{1,2}/;
  const regexTime2 = /\d{1,2}시\d{1,2}/;
  const regexTime3 = /\d{1,2}시/;

  const matchDays: string[] = [];
  const matchTimes: string[] = [];
  const matches: string[] = [];
  const notMatches: string[] = [];

  array.forEach((dateTimeString: string) => {
    let matched = false;
    const targetString = dateTimeString.replace('월', '/').replace('일', '');
    const checkDate1 = targetString.replace(/\s/g, '').match(regexDate);
    const checkDate3 = targetString.replace(/\s/g, '').match(regexDate3);
    const checkTime1 = targetString.replace(/\s/g, '').match(regexTime);
    const checkTime2 = targetString.replace(/\s/g, '').match(regexTime2);
    const checkTime3 = targetString.replace(/\s/g, '').match(regexTime3);

    if (checkDate1) {
      checkDate1.some((date) => {
        if (isValidDateString(date)) {
          matched = true;
          matchDays.push(date);
          return true;
        }
        return false;
      });
    } else if (checkDate3) {
      checkDate3.some((date) => {
        if (isValidDateString(date)) {
          matched = true;
          matchDays.push(date);
          return true;
        }
        return false;
      });
    }

    if (checkTime1) {
      matched = true;
      matchTimes.push(checkTime1[0]);
    } else if (checkTime2) {
      matched = true;
      matchTimes.push(checkTime2[0]);
    } else if (checkTime3) {
      matched = true;
      matchTimes.push(checkTime3[0]);
    }

    if (!matched) {
      notMatches.push(dateTimeString);
    } else {
      matches.push(dateTimeString);
    }
  });

  return {
    matchDays,
    matchTimes,
    matches,
    notMatches,
  };
};

export const getPlannedDatesFromPaymentStatement = (
  saleType: string,
  items: PaymentStatementPurchase[],
  paidDate: Date = new Date(),
): Date[] => {
  const plannedDates: Date[] = [];
  const regexClassSchedule = /\d{1,2}-\d{1,2}-\d{1,2}/;
  const thisYear = new Date(paidDate).getFullYear();
  const date = new Date(paidDate);

  if (items.length > 0) {
    items.forEach((item: PaymentStatementPurchase) => {
      if (saleType === 'commerce') {
        let selectedOptions = [];
        if (item.option && item.option.length > 0) {
          selectedOptions = item.option;
        } else {
          if (!Array.isArray(item.name)) {
            selectedOptions = item.name?.split(' / ') || [];
          } else {
            selectedOptions = item.name;
          }
        }

        const { matchDays, matchTimes } = extractDateFromStrings(selectedOptions);
        if (matchDays && matchDays.length > 0) {
          matchDays.forEach((matchDay: string) => {
            const monthAndDay = matchDay.replace('월', '/').replace('일', '');
            let time = '';
            if (matchTimes.length > 0) {
              time = matchTimes[0].replace('시', ':').replace('분', '');
              if (time.slice(-1) === ':') {
                time = time + '00';
              }
            }

            const thisYearDate = new Date(`${thisYear}/${monthAndDay}${time ? `,${time}` : ''}`);
            const prevYearDate = new Date(`${thisYear - 1}/${monthAndDay}${time ? `,${time}` : ''}`);
            const nextYearDate = new Date(`${thisYear + 1}/${monthAndDay}${time ? `,${time}` : ''}`);

            let minDate = thisYearDate;
            let minDateDiff = Math.abs(date.getTime() - thisYearDate.getTime());

            if (Math.abs(date.getTime() - prevYearDate.getTime()) < minDateDiff) {
              minDateDiff = Math.abs(date.getTime() - prevYearDate.getTime());
              minDate = prevYearDate;
            }

            if (Math.abs(date.getTime() - nextYearDate.getTime()) < minDateDiff) {
              minDateDiff = Math.abs(date.getTime() - nextYearDate.getTime());
              minDate = nextYearDate;
            }

            const utcDate = dateToUtcDate(minDate);
            plannedDates.push(utcDate);
          });
        }
      } else {
        const isStringTypeDate = (item.name as string).replace(/\s/g, '').match(regexClassSchedule);
        if (isStringTypeDate && isStringTypeDate.length > 0) {
          const classSchedule = new Date(item.name as string);
          const utcDate = dateToUtcDate(classSchedule);
          plannedDates.push(utcDate);
        }
      }
    });
    const result = plannedDates.filter((item: Date, index, selfArr) => {
      if (item && typeof item === 'object') {
        return selfArr.findIndex((date) => date.getTime() === item.getTime()) === index;
      }
      return false;
    });
    return result;
  } else {
    return [];
  }
};

export const getPaymentValues = (props: {
  totalClassTimes: number;
  totalStudentCount: number;
  applyGroupDiscount?: boolean;
  classFeePerTimes: number;
}) => {
  const totalClassTimes = props.totalClassTimes || 0;
  const studentCount = props.totalStudentCount || 0;

  const groupSaleChildrenCount =
    props.applyGroupDiscount && studentCount > 1 ? 10 - (studentCount > 4 ? 4 : studentCount) : 10;
  const discountsRatio = groupSaleChildrenCount / 10;
  const totalAmount = props.classFeePerTimes * totalClassTimes * studentCount;
  const discountedAmount = Math.ceil(totalAmount * discountsRatio);
  const discountAmount = Math.ceil(totalAmount - discountedAmount);

  return {
    totalAmount: totalAmount || 0,
    discountedAmount: discountedAmount || 0,
    discountAmount: discountAmount || 0,
    discountPercent: 100 - discountsRatio * 100,
  };
};

export const combinedRefundItemsByName = (refunds?: PaymentStatementRefundItem[]) => {
  return (
    refunds?.reduce((acc: PaymentStatementRefundItem[], cur) => {
      const existingItem = acc.find((item) => {
        if (item.uid) {
          return item.uid === cur.uid;
        }
        return JSON.stringify(item.name) === JSON.stringify(cur.name);
      });

      if (existingItem) {
        existingItem.count += cur.count;
        existingItem.fee += cur.fee * (cur.count || 1);
      } else {
        acc.push({ ...cur, fee: cur.fee * (cur.count || 1) });
      }

      return acc;
    }, []) || []
  );
};

export const getRemainPurchases = (
  purchases: PaymentStatementPurchase[],
  combinedRefundItems: PaymentStatementRefundItem[] = [],
) => {
  return [...purchases]
    .map((purchase) => {
      const found = combinedRefundItems.find((refund) => {
        if (purchase.uid) {
          return refund.uid === purchase.uid;
        }
        return JSON.stringify(refund.name) === JSON.stringify(purchase.name);
      });

      if (found) {
        const foundItemFee = (found.fee || 0) / (found.count || 1);
        const updatedFee = foundItemFee !== purchase.fee ? purchase.fee - foundItemFee : purchase.fee;
        const remainingCount = updatedFee ? purchase.count : purchase.count - found.count;
        return { ...purchase, count: remainingCount, fee: updatedFee };
      }
      return { ...purchase };
    })
    .filter((item) => item.count || item.fee);
};

/**
 * 환불 가능 금액 계산 (공식 통일)
 * @param payment 결제 정보
 * @param refund 환불 정보
 */
export function getRefundableAmount(payment: any, refund: any) {
  return (
    (payment?.amount || 0) +
    (payment?.membershipDiscountAmount || 0) -
    ((refund?.amount || 0) + (refund?.membershipDiscountAmount || 0))
  );
}

/**
 * 옵션별 환불 이력 합산 (구매 옵션별 잔여 수량/금액 계산)
 * @param purchases 구매 옵션 배열
 * @param refunds 환불 이력 배열
 * @returns 옵션별 잔여 수량/금액 배열
 */
export function getOptionRemainings(purchases: any[] = [], refunds: any[] = []) {
  // 옵션별 환불 수량/금액 합산
  const refundMap = new Map();
  refunds.forEach((item) => {
    const key = typeof item.name === 'object' ? JSON.stringify(item.name) : item.name;
    if (!refundMap.has(key)) {
      refundMap.set(key, { count: 0, fee: 0 });
    }
    const prev = refundMap.get(key);
    refundMap.set(key, {
      count: prev.count + (item.count || 0),
      fee: prev.fee + (item.fee || 0),
    });
  });
  return purchases.map((item) => {
    const key = typeof item.name === 'object' ? JSON.stringify(item.name) : item.name;
    const refunded = refundMap.get(key) || { count: 0, fee: 0 };
    return {
      ...item,
      remainCount: (item.count || 0) - (refunded.count || 0),
      remainFee: (item.fee || 0) - (refunded.fee || 0),
    };
  });
}

export enum TossPaymentsPaymentMethodEN {
  CARD = 'CARD',
  EASY_PAY = 'EASY_PAY',
  TRANSFER = 'TRANSFER',
  VIRTUAL_ACCOUNT = 'VIRTUAL_ACCOUNT',
}

export enum TossPaymentsPaymentMethodKR {
  CARD = '카드',
  EASY_PAY = '간편결제',
  TRANSFER = '계좌이체',
  VIRTUAL_ACCOUNT = '가상계좌',
}

export const TOSS_PAYMENTS_CARD_COMPANY_CODE: { [key: string]: string } = {
  '3K': '기업비씨',
  '46': '광주',
  '71': '롯데',
  '30': '산업',
  '31': '비씨',
  '51': '삼성',
  '38': '새마을',
  '41': '신한',
  '62': '신협',
  '36': '씨티',
  '33': '우리',
  W1: '우리',
  '37': '우체국',
  '39': '저축',
  '35': '전북',
  '42': '제주',
  '15': '카카오뱅크',
  '3A': '케이뱅크',
  '24': '토스뱅크',
  '21': '하나',
  '61': '현대',
  '11': '국민',
  '91': '농협',
  '34': '수협',
};

export const getPaymentMethodText = (payment: PaymentStatementPayment) => {
  let paymentMethod = '-';
  const gateway = payment?.gateway || undefined;
  if (gateway && gateway === 'toss') {
    if (payment.method === TossPaymentsPaymentMethodEN.CARD || payment.method === TossPaymentsPaymentMethodKR.CARD) {
      if (payment.result?.data?.card?.issuerCode) {
        paymentMethod = `${TOSS_PAYMENTS_CARD_COMPANY_CODE[`${payment.result.data.card.issuerCode}`]}카드`;
      }
    } else if (
      payment.method === TossPaymentsPaymentMethodEN.EASY_PAY ||
      payment.method === TossPaymentsPaymentMethodKR.EASY_PAY
    ) {
      if (payment.result?.data?.easyPay) {
        const provider = payment.result.data.easyPay.provider;
        if (provider === 'NAVERPAY' || provider === '네이버페이') {
          paymentMethod = '네이버페이';
        } else if (provider === 'KAKAOPAY' || provider === '카카오페이') {
          paymentMethod = '카카오페이';
        } else if (provider === 'TOSSPAY' || provider === '토스페이' || provider === '토스결제') {
          paymentMethod = '토스페이';
        } else {
          paymentMethod = provider;
        }
      }
    } else if (
      payment.method === TossPaymentsPaymentMethodEN.TRANSFER ||
      payment.method === TossPaymentsPaymentMethodKR.TRANSFER
    ) {
      paymentMethod = '계좌이체';
    } else if (
      payment.method === TossPaymentsPaymentMethodEN.VIRTUAL_ACCOUNT ||
      payment.method === TossPaymentsPaymentMethodKR.VIRTUAL_ACCOUNT
    ) {
      paymentMethod = '가상계좌';
    } else if (payment.method === 'point_only') {
      paymentMethod = '포인트결제';
    }
  } else {
    if (payment.method === 'creditPoints') {
      paymentMethod = '포인트결제';
    } else if (payment.method === 'kakaopay') {
      paymentMethod = '카카오페이';
    } else if (payment.method === 'naverpay') {
      paymentMethod = '네이버페이';
    } else if (payment.method === 'card') {
      paymentMethod = '카드결제';
    }
  }
  return paymentMethod;
};
